import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  BooleanFilterControl,
  CHIP_ACTIVE,
  CHIP_BASE,
  CHIP_INACTIVE,
  CombinationFilterOption,
  EnumFilterControl,
  groupCombinationFilterOptions,
  RangeFilterControl
} from 'components/common/combination-filter-controls';
import { combinationFilterOptionIsVisible } from 'components/common/combination-filter-option-visibility';
import AccordionSection from 'components/layout/accordion-section';
import {
  DuctedCompatibilityEntry,
  DuctedResultSet,
  DUCTED_RESULT_SETS
} from 'features/ducted-compatibility';
import {
  DuctedSystemTypeSelection,
  setDuctedCombinationFilters,
  setDuctedEquipmentMatchingOnly,
  setDuctedFuelSelections,
  setDuctedSystemTypeSelections
} from 'features/ui';
import useFilterTooltips from 'hooks/use-filter-tooltips';
import { useMemo } from 'react';
import { KEPT_PAIRING_TAG } from 'utils/compatibility-systems';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import {
  DuctedFuelToken,
  formatKeptSystemTypeLabel,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';
import { recommendedAllTypesCount } from 'utils/recommendations-strip-logic';
import {
  availableDuctedFuels,
  boundedCodesFor,
  ductedUniverseCodes,
  filterSystemTypesByFuel,
  FUEL_CHIP_ORDER,
  mergeCombinationFilterOptions,
  mergeSystemTypeFacets,
  pruneTypeSelectionsToFuels,
  sumFacetCounts,
  sumTotalCounts,
  SystemTypeFacetEntry,
  toggleEnumFilterValue,
  toggleFuelSelection
} from './ducted-facet-merge';

// Spec facet groups suppressed from the ducted rail render (AHA-6796): the
// legacy `fuelType` spec facet can only ever say "Natural Gas" on ducted
// surfaces (LP systems are physically NG furnace models + lp_conversion_kit
// — the spec tables carry zero propane rows), which is redundant and
// misleading next to the token-based Fuel chip row. GUI-side render filter
// only — the payload keeps flowing, and the CCB modal (which has no token
// Fuel filter) deliberately keeps rendering it.
const SUPPRESSED_FACET_FIELDS = new Set(['fuelType']);

// One System Type chip (AHA-6681 kept/alternatives filter split): a code
// present in both result-set universes renders TWO chips — the plain
// alternatives one and the Customer's-formatted kept one — each toggling
// its own scoped selection entry.
type SystemTypeChip = {
  key: string;
  code: string;
  resultSet: DuctedResultSet;
  label: string;
  count: number;
};

// Ducted System Filters section on Choose Systems (AHA-6681 CS-1): renders
// the shared System Type selection and the backend's faceted spec filters,
// merged across every eligible ducted system's results. Fetching lives in
// useDuctedCompatibilityFetch (mounted at page level); this component only
// reads the slice and dispatches filter changes.
export default function DuctedSystemFiltersSection() {
  const dispatch = useAppDispatch();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const resultsBySystem = useAppSelector(
    (state) => state.ductedCompatibility.resultsBySystem
  );
  const compatibleResultsBySystem = useAppSelector(
    (state) => state.structure.compatibleResultsBySystem
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  const {
    filters,
    systemTypeSelections,
    fuelSelections,
    equipmentMatchingOnly
  } = ductedFilters;
  // Equipment Matching (kept-only) narrows every count and chip universe
  // below to the kept-constrained set; the filter's own chips read the
  // unscoped lists so it can always be turned back off.
  const resultSetsInScope = useMemo(
    () =>
      equipmentMatchingOnly
        ? DUCTED_RESULT_SETS.filter((resultSet) => resultSet === 'kept')
        : DUCTED_RESULT_SETS,
    [equipmentMatchingOnly]
  );
  // Admin-managed info tooltips (AHA-6681): code → content keyed by the pnet
  // registry field ids; a code without content renders no icon.
  const filterTooltips = useFilterTooltips();

  const eligibleSystems = useMemo(
    () =>
      getChooseSystemsEligibleDuctedSystems(
        existingSystems,
        recommendationSelectionsBySystem,
        wantCompositionPicksBySystem
      ),
    [
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );
  // Both result sets contribute (AHA-6681 sub-build 4): the kept-constrained
  // and full-alternatives entries of every eligible system merge into one
  // facet/count universe — the shared filter state applies to both, and the
  // totals sum both sets (approved spec).
  const entries = useMemo(
    () =>
      eligibleSystems.flatMap((system) =>
        resultSetsInScope
          .map((resultSet) => resultsBySystem[system.uniqueId]?.[resultSet])
          .filter(
            (entry): entry is DuctedCompatibilityEntry => entry !== undefined
          )
      ),
    [eligibleSystems, resultsBySystem, resultSetsInScope]
  );

  // Chip universe (AHA-6681 requirement D.2, wants-gateway since sub-build
  // 3): every system contributes only the recommended slice of its facet —
  // present-with-count-0 entries survive the code filter, so zero-count
  // chips stay visible (requirement C parity). A system without a resolvable
  // bound (no summary — derivation pending or dead-ended) contributes
  // nothing: there is no browse-everything facet anymore. Each list keeps
  // its result set (AHA-6681 filter split) so the kept and alternatives
  // chip universes can merge separately below.
  const allBoundedFacetLists = useMemo(
    () =>
      eligibleSystems
        .flatMap((system) =>
          DUCTED_RESULT_SETS.map(
            (
              resultSet
            ): {
              resultSet: DuctedResultSet;
              facets: SystemTypeFacetEntry[];
            } | null => {
              const entry = resultsBySystem[system.uniqueId]?.[resultSet];
              if (!entry) return null;
              const bounded = boundedCodesFor(
                compatibleResultsBySystem[system.uniqueId]
              );
              if (bounded === null) return null;
              const boundedSet = new Set(bounded);
              return {
                resultSet,
                facets: entry.systemTypes.filter((st) =>
                  boundedSet.has(st.code)
                )
              };
            }
          )
        )
        .filter((list): list is NonNullable<typeof list> => list !== null),
    [eligibleSystems, resultsBySystem, compatibleResultsBySystem]
  );
  const boundedFacetLists = useMemo(
    () =>
      allBoundedFacetLists.filter((list) =>
        resultSetsInScope.includes(list.resultSet)
      ),
    [allBoundedFacetLists, resultSetsInScope]
  );
  // Equipment Matching chip counts (unscoped, fuel-narrowed like the type
  // chips): the kept-pairing total decides whether the filter is offered.
  const equipmentMatchingCounts = useMemo(() => {
    const countOf = (lists: typeof allBoundedFacetLists) =>
      sumFacetCounts(
        filterSystemTypesByFuel(
          mergeSystemTypeFacets(lists.map((list) => list.facets)),
          fuelSelections
        )
      );
    return {
      all: countOf(allBoundedFacetLists),
      kept: countOf(
        allBoundedFacetLists.filter((list) => list.resultSet === 'kept')
      )
    };
  }, [allBoundedFacetLists, fuelSelections]);
  const showEquipmentMatching =
    equipmentMatchingCounts.kept > 0 || equipmentMatchingOnly;
  const mergedSystemTypes = useMemo(
    () => mergeSystemTypeFacets(boundedFacetLists.map((list) => list.facets)),
    [boundedFacetLists]
  );
  // FULL-universe fuel vocabulary (AHA-6796 fuel chips = full universe):
  // chip PRESENCE comes from the same ductedUniverseCodes →
  // availableDuctedFuels chain the EE fuel row uses (the two surfaces can
  // no longer drift) — every fuel with >=1 candidate type in the eligible
  // catalog universe renders, not just the recommended bound's fuels
  // (which collapse to the derived fuel on an LP-derived project).
  const recommendationCatalogCodesBySystem = useAppSelector(
    (state) => state.structure.recommendationCatalogCodesBySystem
  );
  const universeFuels = useMemo(
    () =>
      availableDuctedFuels(
        ductedUniverseCodes(
          eligibleSystems,
          recommendationCatalogCodesBySystem,
          compatibleResultsBySystem
        )
      ),
    [
      eligibleSystems,
      recommendationCatalogCodesBySystem,
      compatibleResultsBySystem
    ]
  );
  // Fuel chip row (AHA-6796): presence from the universe (above); COUNTS
  // stay the bounded-facet sum across both result sets — a universe fuel
  // with no bounded facet presence shows the honest "(0)" (the requirement
  // C zero-count convention the type chips already use). The row itself
  // never filters by the fuel selection.
  const fuelChips = useMemo(
    () =>
      FUEL_CHIP_ORDER.filter(({ fuel }) => universeFuels.includes(fuel)).map(
        ({ fuel, label }) => ({
          fuel,
          label,
          count: mergedSystemTypes
            .filter((st) => systemTypeCodeFuel(st.code) === fuel)
            .reduce((sum, st) => sum + st.count, 0)
        })
      ),
    [universeFuels, mergedSystemTypes]
  );
  // The type-chip universe under the fuel selection (strict): the chips a
  // user can pick always live within the fuel-narrowed bound, which is what
  // keeps a chip-vs-fuel disjoint fetch structurally impossible (stale
  // selections are pruned on fuel change in toggleFuelChip).
  const fuelFilteredSystemTypes = useMemo(
    () => filterSystemTypesByFuel(mergedSystemTypes, fuelSelections),
    [mergedSystemTypes, fuelSelections]
  );
  // Per-result-set merged universes (AHA-6681 filter split): the kept and
  // alternatives chips carry their own counts.
  const mergedByResultSet = useMemo(() => {
    const merge = (resultSet: DuctedResultSet) =>
      new Map(
        mergeSystemTypeFacets(
          boundedFacetLists
            .filter((list) => list.resultSet === resultSet)
            .map((list) => list.facets)
        ).map((st) => [st.code, st])
      );
    return { kept: merge('kept'), alternatives: merge('alternatives') };
  }, [boundedFacetLists]);
  // Kept component classes across eligible systems (union — pragmatic for
  // the multi-system edge) feed the kept chips' Customer's-formatted titles.
  const unionKeptTypes = useMemo(() => {
    const seen = new Set<string>();
    const types: string[] = [];
    eligibleSystems.forEach((system) => {
      system.components
        .filter((component) => !component.replacing)
        .forEach((component) => {
          if (!seen.has(component.type)) {
            seen.add(component.type);
            types.push(component.type);
          }
        });
    });
    return types;
  }, [eligibleSystems]);
  // Chip list in first-appearance order: a code in BOTH universes renders
  // two chips — the plain alternatives one, then the kept variant titled
  // via formatKeptSystemTypeLabel (formatter null-fallback renders the raw
  // label — two visually identical chips, accepted and flagged for review).
  const systemTypeChips = useMemo(
    () =>
      fuelFilteredSystemTypes.flatMap((st): SystemTypeChip[] => {
        const kept = mergedByResultSet.kept.get(st.code);
        const alternatives = mergedByResultSet.alternatives.get(st.code);
        const chips: SystemTypeChip[] = [];
        if (alternatives) {
          chips.push({
            key: `alternatives:${st.code}`,
            code: st.code,
            resultSet: 'alternatives',
            label: alternatives.label,
            count: alternatives.count
          });
        }
        if (kept) {
          chips.push({
            key: `kept:${st.code}`,
            code: st.code,
            resultSet: 'kept',
            label: formatKeptSystemTypeLabel(
              kept.label,
              st.code,
              unionKeptTypes
            ),
            count: kept.count
          });
        }
        return chips;
      }),
    [fuelFilteredSystemTypes, mergedByResultSet, unionKeptTypes]
  );
  // Union of the per-system recommended bounds — the code set behind the
  // "All recommended" chip count below.
  const recommendedCodesUnion = useMemo(() => {
    const codes: string[] = [];
    const seen = new Set<string>();
    eligibleSystems.forEach((system) => {
      (
        boundedCodesFor(compatibleResultsBySystem[system.uniqueId]) ?? []
      ).forEach((code) => {
        if (!seen.has(code)) {
          seen.add(code);
          codes.push(code);
        }
      });
    });
    return codes;
  }, [eligibleSystems, compatibleResultsBySystem]);
  const groupedOptions = useMemo(
    () =>
      groupCombinationFilterOptions(
        mergeCombinationFilterOptions(
          entries.map((entry) => entry.filterOptions)
        ).filter((option) => !SUPPRESSED_FACET_FIELDS.has(option.field))
      ),
    [entries]
  );
  const totalCount = useMemo(
    () => sumTotalCounts(entries.map((entry) => entry.totalCount)),
    [entries]
  );

  const isLoading = entries.some((entry) => entry.status === 'loading');
  const hasError = entries.some((entry) => entry.status === 'error');
  // Every eligible system hit the unanchored-without-codes guard: no facet
  // exists to pick a type from, so explain instead of rendering nothing.
  const allSkipped =
    entries.length > 0 && entries.every((entry) => entry.status === 'skipped');
  // Copy variant matching the rail's (AHA-6681 want-only fallback): with
  // wants saved, all-skipped means every selected want type is unmapped.
  const anyWantsSaved = eligibleSystems.some(
    (system) =>
      (recommendationSelectionsBySystem[system.uniqueId]?.wantIds?.length ??
        0) > 0
  );

  if (eligibleSystems.length === 0) return null;

  // Active filter readers (same shapes as the CCB modal's)
  const rangeValueOf = (field: string): { min?: number; max?: number } => {
    const found = filters.find((f) => f.field === field && f.kind === 'range');
    return found && found.kind === 'range'
      ? { min: found.min, max: found.max }
      : {};
  };
  const enumValuesOf = (field: string): string[] => {
    const found = filters.find((f) => f.field === field && f.kind === 'enum');
    return found && found.kind === 'enum' ? found.values : [];
  };
  const booleanValueOf = (field: string): boolean | undefined => {
    const found = filters.find(
      (f) => f.field === field && f.kind === 'boolean'
    );
    return found && found.kind === 'boolean' ? found.value : undefined;
  };

  // Active filter writers (immutable upserts keyed by field, dispatched to
  // the shared ui state)
  const setRangeFilter = (
    field: string,
    min: number | undefined,
    max: number | undefined
  ) => {
    const rest = filters.filter((f) => f.field !== field);
    dispatch(
      setDuctedCombinationFilters(
        min === undefined && max === undefined
          ? rest
          : [...rest, { field, kind: 'range', min, max }]
      )
    );
  };
  // Shared upsert helper (AHA-6796): the EE chassis row writes the same
  // shape through the same helper, so the two surfaces cannot diverge.
  const toggleEnumFilter = (field: string, value: string) => {
    dispatch(
      setDuctedCombinationFilters(toggleEnumFilterValue(filters, field, value))
    );
  };
  const setBooleanFilter = (field: string, value: boolean | undefined) => {
    const rest = filters.filter((f) => f.field !== field);
    dispatch(
      setDuctedCombinationFilters(
        value === undefined
          ? rest
          : [...rest, { field, kind: 'boolean', value }]
      )
    );
  };

  const chipSelected = (chip: SystemTypeChip) =>
    systemTypeSelections.some(
      (selection) =>
        selection.code === chip.code && selection.resultSet === chip.resultSet
    );
  const toggleSystemTypeChip = (chip: SystemTypeChip) => {
    const next: DuctedSystemTypeSelection[] = chipSelected(chip)
      ? systemTypeSelections.filter(
          (selection) =>
            !(
              selection.code === chip.code &&
              selection.resultSet === chip.resultSet
            )
        )
      : [
          ...systemTypeSelections,
          { code: chip.code, resultSet: chip.resultSet }
        ];
    dispatch(setDuctedSystemTypeSelections(next));
  };

  // Fuel toggle = multi-select union (AHA-6796). On every change the type
  // selection is pruned to codes of the still-selected fuels, so a stale
  // chip can never sit disjoint from the fuel-narrowed bound (both
  // dispatches batch into one render). Shared toggle/prune helpers — the
  // EE fuel row runs the exact same pair.
  const toggleFuelChip = (fuel: DuctedFuelToken) => {
    const next = toggleFuelSelection(fuelSelections, fuel);
    dispatch(setDuctedFuelSelections(next));
    const pruned: DuctedSystemTypeSelection[] = pruneTypeSelectionsToFuels(
      systemTypeSelections,
      next
    );
    if (pruned !== systemTypeSelections) {
      dispatch(setDuctedSystemTypeSelections(pruned));
    }
  };

  // Turning Equipment Matching on hides the alternatives chips, so any
  // alternatives-scoped type selection is pruned with it — a selection the
  // user can no longer see must not keep counting in the badge.
  const setEquipmentMatching = (only: boolean) => {
    if (only === equipmentMatchingOnly) return;
    dispatch(setDuctedEquipmentMatchingOnly(only));
    if (!only) return;
    const pruned = systemTypeSelections.filter(
      (selection) => selection.resultSet === 'kept'
    );
    if (pruned.length !== systemTypeSelections.length) {
      dispatch(setDuctedSystemTypeSelections(pruned));
    }
  };

  // Renders a single filter control, or null when the facet has no data to
  // offer (the modal's null-hiding rules, minus its structure-requirements
  // coupling — that mode is modal-only).
  const renderFilterControl = (
    option: CombinationFilterOption,
    info?: string,
    showLabel = true
  ) => {
    if (option.kind === 'range') {
      if (option.min == null || option.max == null) return null;
      const value = rangeValueOf(option.field);
      return (
        <RangeFilterControl
          key={option.field}
          option={option}
          info={info}
          showLabel={showLabel}
          min={value.min}
          max={value.max}
          onCommit={(min, max) => setRangeFilter(option.field, min, max)}
        />
      );
    }
    if (option.kind === 'enum') {
      // Selected-but-absent values (AHA-6796 chassis seed no-wedge
      // guarantee): an active enum value the facet doesn't offer — e.g. a
      // seeded cabinetChassisSize no combo in the universe carries — must
      // still render as a clearable zero-count chip, or the filter would be
      // invisible and unclearable while matching nothing. Applies to every
      // active enum value, manual selections included.
      const selected = enumValuesOf(option.field);
      const missingSelected = selected.filter(
        (value) => !option.values.some((opt) => opt.value === value)
      );
      const augmented =
        missingSelected.length === 0
          ? option
          : {
              ...option,
              values: [
                ...option.values,
                ...missingSelected.map((value) => ({ value, count: 0 }))
              ]
            };
      if (augmented.values.length === 0) return null;
      return (
        <EnumFilterControl
          key={option.field}
          option={augmented}
          selected={selected}
          showLabel={showLabel}
          onToggle={(value) => toggleEnumFilter(option.field, value)}
        />
      );
    }
    if (option.trueCount + option.falseCount === 0) return null;
    return (
      <BooleanFilterControl
        key={option.field}
        option={option}
        showLabel={showLabel}
        value={booleanValueOf(option.field)}
        onChange={(value) => setBooleanFilter(option.field, value)}
      />
    );
  };

  // The honest "All recommended" number (AHA-6681 sub-build 3): the facet
  // counts summed over the recommended codes only — the All-Types
  // browse-everything total is gone. Under a Fuel selection it counts only
  // the matching-fuel types (AHA-6796), same universe as the chip row.
  const totalAvailable = recommendedAllTypesCount(
    fuelFilteredSystemTypes,
    recommendedCodesUnion
  );

  // Fields of a group's options that hold an active filter — the badge count.
  const activeFilterCountFor = (options: CombinationFilterOption[]): number => {
    const fields = new Set(options.map((option) => option.field));
    return filters.filter((f) => fields.has(f.field)).length;
  };

  return (
    <>
      {showEquipmentMatching && (
        <AccordionSection
          title='Equipment Matching'
          badge={equipmentMatchingOnly ? 1 : null}
          info={filterTooltips.equipmentMatching}
        >
          <div className='flex flex-wrap gap-1'>
            <button
              type='button'
              className={`${CHIP_BASE} ${
                equipmentMatchingOnly ? CHIP_INACTIVE : CHIP_ACTIVE
              }`}
              onClick={() => setEquipmentMatching(false)}
            >
              All systems ({equipmentMatchingCounts.all})
            </button>
            <button
              type='button'
              className={`${CHIP_BASE} ${
                equipmentMatchingOnly ? CHIP_ACTIVE : CHIP_INACTIVE
              }`}
              onClick={() => setEquipmentMatching(true)}
            >
              {KEPT_PAIRING_TAG} ({equipmentMatchingCounts.kept})
            </button>
          </div>
        </AccordionSection>
      )}
      {fuelChips.length > 0 && (
        <AccordionSection
          title='Fuel'
          badge={fuelSelections.length || null}
          info={filterTooltips.fuel}
        >
          <div className='flex flex-wrap gap-1'>
            {fuelChips.map((chip) => (
              <button
                type='button'
                key={chip.fuel}
                className={`${CHIP_BASE} ${
                  fuelSelections.includes(chip.fuel)
                    ? CHIP_ACTIVE
                    : CHIP_INACTIVE
                }`}
                onClick={() => toggleFuelChip(chip.fuel)}
              >
                {chip.label} ({chip.count})
              </button>
            ))}
          </div>
        </AccordionSection>
      )}
      <AccordionSection
        title='System Type'
        badge={systemTypeSelections.length || null}
        info={filterTooltips.systemType}
      >
        {allSkipped ? (
          <div className='text-xs text-gray-500'>
            {anyWantsSaved
              ? "Your selected system types don't have searchable systems yet — adjust what you want on the Existing Equipment step."
              : 'Choose what you want on the Existing Equipment step to see recommended systems.'}
          </div>
        ) : (
          <div className='flex flex-wrap gap-1'>
            <button
              type='button'
              className={`${CHIP_BASE} ${
                systemTypeSelections.length === 0 ? CHIP_ACTIVE : CHIP_INACTIVE
              }`}
              onClick={() => dispatch(setDuctedSystemTypeSelections([]))}
            >
              All recommended ({totalAvailable})
            </button>
            {systemTypeChips.map((chip) => (
              <button
                type='button'
                key={chip.key}
                className={`${CHIP_BASE} ${
                  chipSelected(chip) ? CHIP_ACTIVE : CHIP_INACTIVE
                }`}
                onClick={() => toggleSystemTypeChip(chip)}
              >
                {chip.label} ({chip.count ?? 0})
              </button>
            ))}
          </div>
        )}
      </AccordionSection>
      {groupedOptions.map(({ group, options }) => {
        const visibleOptions = options.filter((option) =>
          combinationFilterOptionIsVisible(
            option,
            option.kind === 'enum' ? enumValuesOf(option.field) : []
          )
        );
        // Tooltip placement (AHA-6681): a section with one visible field
        // (Refrigerant, Tonnage, …) gets a header icon keyed by that field
        // id and skips the field label — it would repeat the section title.
        // Multi-field groups (Performance, Capacity) keep each field label
        // and icon that field instead.
        const isMultiField = visibleOptions.length > 1;
        const controls = visibleOptions
          .map((option) =>
            renderFilterControl(
              option,
              isMultiField ? filterTooltips[option.field] : undefined,
              isMultiField
            )
          )
          .filter(Boolean);
        if (controls.length === 0) return null;
        return (
          <AccordionSection
            key={group}
            title={group}
            info={
              isMultiField ? undefined : filterTooltips[visibleOptions[0].field]
            }
            badge={activeFilterCountFor(options) || null}
          >
            <div className='flex flex-col gap-3'>{controls}</div>
          </AccordionSection>
        );
      })}
      <div className='py-2 text-xs text-gray-600'>
        {hasError ? (
          <span className='text-red-600'>
            Couldn&apos;t load compatible systems
          </span>
        ) : (
          <span>
            {totalCount} compatible system{totalCount === 1 ? '' : 's'}
          </span>
        )}
        {isLoading && <span className='text-gray-400'> — updating…</span>}
      </div>
    </>
  );
}
